import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Users, 
  Mail, 
  AlertCircle, 
  Search, 
  ChevronUp, 
  ChevronDown,
  Filter,
  X,
  Building,
  MapPinned
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PresenceOfSamajPage = () => {
  const [presenceData, setPresenceData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'families', direction: 'desc' });

  useEffect(() => {
    const fetchPresenceData = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/presence-data`);
        setPresenceData(response.data.data);
        setSummary(response.data.summary);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching presence data:', err);
        setError('Failed to load presence data');
        setLoading(false);
      }
    };

    fetchPresenceData();
  }, []);

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = presenceData;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.place.toLowerCase().includes(term) ||
        item.state.toLowerCase().includes(term) ||
        item.district.toLowerCase().includes(term)
      );
    }

    // Apply state filter
    if (selectedState) {
      filtered = filtered.filter(item => item.state === selectedState);
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = sortConfig.key === 'families' ? 0 : '';
      if (bVal === null || bVal === undefined) bVal = sortConfig.key === 'families' ? 0 : '';

      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }
      
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return filtered;
  }, [presenceData, searchTerm, selectedState, sortConfig]);

  // Calculate filtered totals
  const filteredTotals = useMemo(() => {
    const totalFamilies = filteredAndSortedData.reduce((sum, item) => sum + (item.families || 0), 0);
    const totalPlaces = filteredAndSortedData.length;
    const uniqueStates = [...new Set(filteredAndSortedData.map(item => item.state))];
    return { totalFamilies, totalPlaces, totalStates: uniqueStates.length };
  }, [filteredAndSortedData]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedState('');
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronUp className="w-4 h-4 text-gray-300" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-primary" />
      : <ChevronDown className="w-4 h-4 text-primary" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading presence data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center text-red-600">
          <AlertCircle className="w-16 h-16 mx-auto mb-4" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="presence-page">
      {/* Hero Section */}
      <section className="gradient-orange py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-white/80 text-sm mb-6">
            <a href="/" className="hover:text-white">Home</a>
            <span>›</span>
            <span>Presence of Samaj</span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              समाज की उपस्थिति
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Presence of Samaj - Our community presence across India and worldwide
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction Paragraph */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-8 mb-8"
        >
          <p className="text-gray-700 text-lg leading-relaxed mb-4">
            मारवाड़ी ब्रह्मक्षत्रिय समाज के बंधु भारत भर में निवास करते हैं। राजस्थान के जोधपुर, बाड़मेर, बालोतरा, जैसलमेर, जालोर, पाली, बीकानेर जिलों में हमारे परिवारों की संख्या अधिक है। राजस्थान के कुछ और जिलों, गुजरात, महाराष्ट्र, छत्तीसगढ़, आंध्र प्रदेश, कर्नाटक व तमिलनाडु आदि राज्यों में भी हमारे बंधु रहते हैं, जो व्यापार, रोजगार के उद्देश्य से मारवाड़ क्षेत्र से वहां जाकर बस गये हैं।
          </p>
          <p className="text-gray-700 text-lg leading-relaxed">
            कुछ परिवार विदेशों में हैं। पड़ौसी देश पाकिस्तान के सिंध प्रांत में ब्रह्मक्षत्रिय (खत्री) परिवार काफी हैं, जो 1947 के विभाजन के बाद वहीं रह गये। उनमें से अनेक परिवारों का सीधा रिश्ता मारवाड़ के परिवारों से है। 1971 व बाद में भी कयी परिवार सिंध से आकर बाड़मेर, जोधपुर, डीसा आदि क्षेत्रों में बसे हैं। सिंध के अतिरिक्त यू एस ए, यू के, ऑस्ट्रेलिया, चीन, सिंगापुर, कनाडा आदि देशों में हमारे समाज के परिवार बस रहे हैं, जो अध्ययन व रोजगार के उद्देश्य से वहां गये हुये हैं व कुछ वहीं बस गये हैं।
          </p>
        </motion.div>

        {/* Request Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-primary/5 to-orange-50 rounded-2xl p-8 mb-12 border border-primary/20"
        >
          <p className="text-gray-800 text-lg leading-relaxed mb-6">
            मारवाड़ी ब्रह्मक्षत्रिय परिवारों की संख्या अनुमानतः सात हजार है। उनमें से अधिकांश की स्थानवार संख्या यहां दी जा रही है। निवेदन है कि :
          </p>
          
          <ul className="space-y-3 text-gray-700 mb-6">
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">•</span>
              <span>जिन स्थानों की परिवारों की संख्या खाली है, परिवारों की संख्या हमें सूचित करें।</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">•</span>
              <span>जिन स्थानों पर परिवारों की संख्या लिख रखी हैं, उनमें संशोधन की आवश्यकता हो तो सप्रमाण हमें सूचित करें।</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">•</span>
              <span>जिन स्थानों पर हमारे परिवार हैं लेकिन उन स्थानों को इस सूची में हम शामिल नहीं कर पाये, उस स्थान का नाम व परिवारों की संख्या हमें सूचित करें।</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">•</span>
              <span>विदेश में बस रहे हमारे परिवारों की आधिकारिक सूचना देने का आग्रह है।</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">•</span>
              <span>परिवारों व परिवारों के सदस्यों के विस्तृत विवरण फीड करने के लिये <a href="/family-registration" className="text-primary underline hover:text-primary-600 font-medium">लिंक</a> का उपयोग करें। इससे परिवारों का रजिस्टर बनेगा।</span>
            </li>
          </ul>
          
          <div className="flex flex-wrap items-center gap-2 p-4 bg-white rounded-lg border border-gray-200">
            <Mail className="w-5 h-5 text-primary flex-shrink-0" />
            <span className="text-gray-700">Please send update or modification requests to:</span>
            <a href="mailto:brahmkhatrimahasabha@gmail.com" className="text-primary font-semibold hover:underline break-all">
              brahmkhatrimahasabha@gmail.com
            </a>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
        >
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 text-center" data-testid="total-families-card">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-3xl font-bold text-primary mb-1" data-testid="total-families-count">
              {summary.totalFamilies?.toLocaleString()}
            </h2>
            <p className="text-gray-600 text-sm">कुल परिवार / Total Families</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 text-center" data-testid="total-places-card">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <MapPinned className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold text-blue-600 mb-1" data-testid="total-places-count">
              {summary.totalPlaces?.toLocaleString()}
            </h2>
            <p className="text-gray-600 text-sm">कुल स्थान / Total Places</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 text-center" data-testid="total-states-card">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Building className="w-6 h-6 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-green-600 mb-1" data-testid="total-states-count">
              {summary.totalStates}
            </h2>
            <p className="text-gray-600 text-sm">राज्य / States</p>
          </div>
        </motion.div>

        {/* Search and Filter Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8"
        >
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            {/* Search Input */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by place, state, or district..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                data-testid="search-input"
              />
            </div>

            {/* State Filter */}
            <div className="relative w-full md:w-64">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none appearance-none bg-white cursor-pointer"
                data-testid="state-filter"
              >
                <option value="">All States</option>
                {summary.states?.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>

            {/* Clear Filters */}
            {(searchTerm || selectedState) && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-2 px-4 py-3 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>

          {/* Filtered Results Summary */}
          {(searchTerm || selectedState) && (
            <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-4 text-sm">
              <span className="text-gray-600">
                Showing <span className="font-semibold text-primary">{filteredTotals.totalPlaces}</span> places
              </span>
              <span className="text-gray-600">
                with <span className="font-semibold text-primary">{filteredTotals.totalFamilies.toLocaleString()}</span> families
              </span>
              <span className="text-gray-600">
                across <span className="font-semibold text-primary">{filteredTotals.totalStates}</span> states
              </span>
            </div>
          )}
        </motion.div>

        {/* Data Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mb-12"
        >
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="presence-table">
              <thead className="bg-gradient-to-r from-primary/5 to-orange-50">
                <tr>
                  <th className="px-6 py-4 text-left">
                    <button
                      onClick={() => handleSort('state')}
                      className="flex items-center gap-2 font-semibold text-gray-700 hover:text-primary transition-colors"
                      data-testid="sort-state-btn"
                    >
                      State / राज्य
                      <SortIcon columnKey="state" />
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left hidden md:table-cell">
                    <button
                      onClick={() => handleSort('district')}
                      className="flex items-center gap-2 font-semibold text-gray-700 hover:text-primary transition-colors"
                      data-testid="sort-district-btn"
                    >
                      District / जिला
                      <SortIcon columnKey="district" />
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left">
                    <button
                      onClick={() => handleSort('place')}
                      className="flex items-center gap-2 font-semibold text-gray-700 hover:text-primary transition-colors"
                      data-testid="sort-place-btn"
                    >
                      Place / स्थान
                      <SortIcon columnKey="place" />
                    </button>
                  </th>
                  <th className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleSort('families')}
                      className="flex items-center gap-2 font-semibold text-gray-700 hover:text-primary transition-colors ml-auto"
                      data-testid="sort-families-btn"
                    >
                      Families / परिवार
                      <SortIcon columnKey="families" />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredAndSortedData.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                      <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>No results found for your search.</p>
                      <button
                        onClick={clearFilters}
                        className="mt-2 text-primary hover:underline"
                      >
                        Clear filters
                      </button>
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedData.map((item, index) => (
                    <tr 
                      key={index} 
                      className="hover:bg-orange-50/50 transition-colors"
                      data-testid={`presence-row-${index}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                          <span className="text-gray-900 font-medium">{item.state}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        <span className="text-gray-600">{item.district || '-'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-900">{item.place}</span>
                        {item.district && (
                          <span className="md:hidden text-xs text-gray-500 block">
                            {item.district}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {item.families > 0 ? (
                          <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                            {item.families.toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {/* Table Footer with Sum */}
              <tfoot className="bg-gradient-to-r from-primary/10 to-orange-100">
                <tr>
                  <td colSpan="3" className="px-6 py-4 font-semibold text-gray-900">
                    Total / कुल योग
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-full font-bold" data-testid="total-sum">
                      {filteredTotals.totalFamilies.toLocaleString()} परिवार
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default PresenceOfSamajPage;
